import React, { useState } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { orderService } from "../services/apiService";

const CustomerPage = () => {
  const [mobile, setMobile] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Selected customer detail
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderPage, setOrderPage] = useState(0);
  const [orderTotalPages, setOrderTotalPages] = useState(0);

  const searchCustomers = async (pageNo = 0) => {
    if (!mobile.trim()) {
      setToast({ type: "error", message: "Enter mobile number to search" });
      return;
    }
    setLoading(true);
    setSelectedCustomer(null);
    setOrders([]);
    try {
      const data = await orderService.searchCustomers({
        mobileNumber: mobile.trim() || null,
        page: pageNo,
        size: 10,
      });
      setCustomers(data.content || []);
      setTotalPages(data.totalPages || 0);
      setPage(pageNo);
    } catch {
      setCustomers([]);
      setToast({ type: "error", message: "Failed to search customers" });
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setOrderPage(0);
    await fetchOrders(customer.mobileNumber, 0);
  };

  const fetchOrders = async (mobileNo, pageNo) => {
    setOrdersLoading(true);
    try {
      const data = await orderService.getCustomerOrders(mobileNo, pageNo, 10);
      setOrders(data.content || []);
      setOrderTotalPages(data.totalPages || 0);
      setOrderPage(pageNo);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Search */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          &#x1F465; Customer Search
        </h2>
        <div className="p-2">
          <form onSubmit={(e) => { e.preventDefault(); searchCustomers(0); }} className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number</label>
              <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile" maxLength="10"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <button type="submit" disabled={loading}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
              }`}>
              {loading ? <><i className="fas fa-spinner fa-spin" /> Searching...</> : <><i className="fas fa-search" /> Search</>}
            </button>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            &#x1F4CB; {selectedCustomer ? `${selectedCustomer.customerName || "Customer"} - Orders` : "Results"}
            {customers.length > 0 && !selectedCustomer && <span className="text-xs font-normal text-blue-600 ml-2">({customers.length})</span>}
          </h2>
          {selectedCustomer && (
            <button onClick={() => { setSelectedCustomer(null); setOrders([]); }}
              className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
              <i className="fas fa-arrow-left" /> Back
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Search Results</h3>
              <span className="text-xs bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">{customers.length} found</span>
            </div>
            <div className="divide-y divide-gray-100">
              {customers.map((c) => (
                <div key={c.customerId} onClick={() => selectCustomer(c)}
                  className="flex items-center justify-between px-5 py-4 hover:bg-indigo-50 cursor-pointer transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <i className="fas fa-user text-indigo-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{c.customerName || "—"}</p>
                      <p className="text-xs text-gray-500">{c.mobileNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <p className="text-xs text-gray-400">Orders</p>
                      <p className="text-sm font-bold text-gray-700">{c.totalOrders}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Total Spent</p>
                      <p className="text-sm font-bold text-blue-600">₹{c.totalSpent?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Due</p>
                      <p className={`text-sm font-bold ${c.totalDue > 0 ? "text-red-600" : "text-green-600"}`}>
                        ₹{c.totalDue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <i className="fas fa-chevron-right text-gray-300"></i>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
                <button onClick={() => searchCustomers(page - 1)} disabled={page === 0}
                  className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
                <button onClick={() => searchCustomers(page + 1)} disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
              </div>
            )}
          </div>
        

        {/* Selected Customer Profile + Orders */}
        {selectedCustomer && (
          <div className="space-y-4 animate-fadeIn">
            {/* Back button */}
            <button onClick={() => { setSelectedCustomer(null); setOrders([]); }}
              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
              <i className="fas fa-arrow-left"></i> Back to results
            </button>

            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <i className="fas fa-user text-white text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{selectedCustomer.customerName || "Unknown"}</h2>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <i className="fas fa-phone text-xs"></i> {selectedCustomer.mobileNumber}
                    </p>
                    {selectedCustomer.address && (
                      <p className="text-xs text-gray-400 mt-0.5"><i className="fas fa-map-marker-alt mr-1"></i>{selectedCustomer.address}</p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-blue-500 font-medium">Total Orders</p>
                    <p className="text-xl font-bold text-blue-700">{selectedCustomer.totalOrders}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-green-500 font-medium">Total Spent</p>
                    <p className="text-lg font-bold text-green-700">₹{selectedCustomer.totalSpent?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-emerald-500 font-medium">Total Paid</p>
                    <p className="text-lg font-bold text-emerald-700">₹{selectedCustomer.totalPaid?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className={`${selectedCustomer.totalDue > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"} border rounded-lg px-4 py-3 text-center`}>
                    <p className={`text-xs font-medium ${selectedCustomer.totalDue > 0 ? "text-red-500" : "text-green-500"}`}>Total Due</p>
                    <p className={`text-lg font-bold ${selectedCustomer.totalDue > 0 ? "text-red-700" : "text-green-700"}`}>
                      ₹{selectedCustomer.totalDue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Order History */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <i className="fas fa-history text-indigo-500"></i> Purchase History
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase">#</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase">Bill No</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Amount</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Paid</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Due</th>
                      <th className="py-3 px-4 text-center text-xs font-semibold uppercase">Status</th>
                      <th className="py-3 px-4 text-center text-xs font-semibold uppercase">Payment</th>
                      <th className="py-3 px-4 text-center text-xs font-semibold uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ordersLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-10">
                          <i className="fas fa-circle-notch fa-spin text-indigo-500 text-xl"></i>
                        </td>
                      </tr>
                    ) : orders.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-10 text-gray-400">
                          <i className="fas fa-inbox text-3xl mb-2 block"></i>
                          <p className="text-sm">No purchase history found</p>
                        </td>
                      </tr>
                    ) : (
                      orders.map((o, idx) => (
                        <tr key={o.orderId} className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50 transition-colors`}>
                          <td className="py-3 px-4 text-sm text-gray-500">{orderPage * 10 + idx + 1}</td>
                          <td className="py-3 px-4 text-sm font-medium text-indigo-700">{o.billNo || `#${o.orderId}`}</td>
                          <td className="py-3 px-4 text-right text-sm text-gray-700">₹{o.finalAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-3 px-4 text-right text-sm text-green-600 font-medium">₹{o.paidAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-3 px-4 text-right">
                            {o.dueAmount > 0 ? (
                              <span className="text-sm font-bold text-red-600">₹{o.dueAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            ) : (
                              <span className="text-sm text-green-600">₹0.00</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                              o.paidType === "F" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                            }`}>
                              <i className={`fas ${o.paidType === "F" ? "fa-check-circle" : "fa-clock"} text-[10px]`}></i>
                              {o.paidType === "F" ? "Paid" : "Partial"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{o.paymentMode || "—"}</span>
                          </td>
                          <td className="py-3 px-4 text-center text-xs text-gray-500">
                            {o.createdOn ? new Date(o.createdOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {orderTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
                  <button onClick={() => fetchOrders(selectedCustomer.mobileNumber, orderPage - 1)} disabled={orderPage === 0}
                    className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                  <span className="text-xs text-gray-500">Page {orderPage + 1} of {orderTotalPages}</span>
                  <button onClick={() => fetchOrders(selectedCustomer.mobileNumber, orderPage + 1)} disabled={orderPage >= orderTotalPages - 1}
                    className="px-3 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next →</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {customers.length === 0 && !loading && !selectedCustomer && (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-user-search text-4xl mb-3 block" />
            <p className="text-sm">Search for a customer by mobile number</p>
          </div>
        )}
        </div>
      </section>

      <style>{`
        .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default CustomerPage;
